// src/pages/system/InternetPlans.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

const PlanForm = ({ open, onClose, onSubmit, plan = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    download_speed: '',
    upload_speed: '',
    description: '',
  });

  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (plan) {
        setFormData({
          name: plan.name || '',
          price: plan.price || '',
          download_speed: plan.download_speed || '',
          upload_speed: plan.upload_speed || '',
          description: plan.description || '',
        });
      } else {
        setFormData({
          name: '',
          price: '',
          download_speed: '',
          upload_speed: '',
          description: '',
        });
      }
      setError('');
    }
  }, [plan, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validaciones
    if (parseFloat(formData.price) <= 0) {
      setError('El precio debe ser mayor a 0');
      return;
    }

    if (parseInt(formData.download_speed) <= 0 || parseInt(formData.upload_speed) <= 0) {
      setError('Las velocidades deben ser mayores a 0');
      return;
    }

    const result = await onSubmit(formData);
    if (result && !result.success) {
      setError(result.error || 'Error al guardar el plan');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {plan ? 'Editar Plan' : 'Nuevo Plan'}
        </DialogTitle>
        <DialogContent>
          <Box className="space-y-4 py-4">
            {error && (
              <Alert severity="error" className="mb-4">
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Nombre del Plan"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />

            <TextField
              fullWidth
              label="Precio"
              name="price"
              type="number"
              value={formData.price}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: 'S/.'
              }}
            />

            <Box className="grid grid-cols-2 gap-4">
              <TextField
                label="Velocidad de Descarga"
                name="download_speed"
                type="number"
                value={formData.download_speed}
                onChange={handleChange}
                required
                InputProps={{
                  endAdornment: 'Mbps'
                }}
              />

              <TextField
                label="Velocidad de Subida"
                name="upload_speed"
                type="number"
                value={formData.upload_speed}
                onChange={handleChange}
                required
                InputProps={{
                  endAdornment: 'Mbps'
                }}
              />
            </Box>

            <TextField
              fullWidth
              label="Descripción"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" color="primary">
            Guardar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const InternetPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch('/api/plans', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setPlans(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      const method = selectedPlan ? 'PUT' : 'POST';
      const url = selectedPlan
        ? `/api/plans/${selectedPlan.id}`
        : '/api/plans';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al guardar el plan');
      }

      fetchPlans();
      setOpenForm(false);
      setSelectedPlan(null);
      return { success: true };
    } catch (error) {
      console.error('Error saving plan:', error);
      return { success: false, error: error.message };
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`/api/plans/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Error al eliminar el plan');

      fetchPlans();
      setDeleteConfirmOpen(false);
      setPlanToDelete(null);
    } catch (error) {
      console.error('Error deleting plan:', error);
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Nombre',
      width: 200
    },
    {
      field: 'price',
      headerName: 'Precio',
      width: 130,
      type: 'number',
      valueFormatter: (params) => {
        const value = params.value || 0;
        return `S/ ${Number(value).toFixed(2)}`;
      }
    },
    {
      field: 'download_speed',
      headerName: 'Descarga',
      width: 130,
      valueFormatter: (params) => {
        const value = params.value || 0;
        return `${Number(value)} Mbps`;
      }
    },
    {
      field: 'upload_speed',
      headerName: 'Subida',
      width: 130,
      valueFormatter: (params) => {
        const value = params.value || 0;
        return `${Number(value)} Mbps`;
      }
    },
    {
      field: 'description',
      headerName: 'Descripción',
      width: 300
    },
    {
      field: 'client_count',
      headerName: 'Clientes',
      width: 100,
      type: 'number',
      valueFormatter: (params) => {
        const value = params.value || 0;
        return Number(value);
      }
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedPlan(params.row);
                setOpenForm(true);
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          {(params.row.client_count === 0) && (
            <Tooltip title="Eliminar">
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setPlanToDelete(params.row);
                  setDeleteConfirmOpen(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box className="p-6">
      <Box className="flex justify-between items-center mb-6">
        <Typography variant="h4">Planes de Internet</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedPlan(null);
            setOpenForm(true);
          }}
        >
          Nuevo Plan
        </Button>
      </Box>

      <DataGrid
        rows={plans}
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[10, 25, 50]}
        disableSelectionOnClick
        loading={loading}
        autoHeight
        className="bg-white"
      />

      <PlanForm
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setSelectedPlan(null);
        }}
        onSubmit={handleSubmit}
        plan={selectedPlan}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          ¿Estás seguro de que deseas eliminar el plan "{planToDelete?.name}"?
          {planToDelete?.client_count > 0 && (
            <Typography color="error" className="mt-2">
              ¡Advertencia! Este plan tiene {planToDelete.client_count} clientes asignados.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => handleDelete(planToDelete.id)}
            color="error"
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InternetPlans;